import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { camelize, getRandomString } from '../../utils';
import { defaultFormFieldsData } from './data';

export type BoxSchemaType = {
  type: string;
  hasBody: boolean;
  fields: string[];
  instances: { default: number; max: number; current?: number };
};

export interface SchemaState {
  type: string;
  fieldData: { id: string; fields: { [key: string]: string } }[];
  deletedField: { id: string; fields: { [key: string]: string } }[];
  instances: { default: number; max: number; current: number };
}

const localStoreState = JSON.parse(
  localStorage.getItem('tk-template-store') || '{}'
);

const initialState: SchemaState = (localStoreState.schema as SchemaState) || {
  type: '@init',
  instances: { default: 0, max: 0, current: 0 },
  fieldData: [],
  deletedField: [],
};

const getDefaultFields = () => {
  const newObj: Record<string, string> = {};
  Object.keys(defaultFormFieldsData).forEach((key) => {
    newObj[camelize(key)] = defaultFormFieldsData[key];
  });
  return newObj;
};
const generatedFieldsArr = (length: number) =>
  Array.from({
    length,
  }).map(() => ({ id: getRandomString(), fields: getDefaultFields() }));

export const schemaSlice = createSlice({
  name: 'schema',
  initialState,
  reducers: {
    initBlockData: (
      state,
      action: PayloadAction<{
        type: string;
        instances: { default: number; max: number };
      }>
    ) => {
      const { type, instances } = action.payload;
      // Since we are loading the state from the store.
      // We don't need to perform this if the template didn't change. Hence Early return.
      if (type === state.type) return;

      const currInstance = state.instances.current || 0;
      const incomingInstance = instances.default;
      const insDiff = incomingInstance - currInstance;

      if (currInstance < incomingInstance) {
        const combinedArr = [...state.fieldData, ...state.deletedField];
        let slicedArr = combinedArr.slice(0, incomingInstance);
        const newDeletedArr = state.deletedField.slice(insDiff);
        if (slicedArr.length < incomingInstance) {
          const insDiffAgain = incomingInstance - slicedArr.length;
          slicedArr = [...slicedArr, ...generatedFieldsArr(insDiffAgain)];
        }
        state.fieldData = slicedArr;
        state.deletedField = newDeletedArr;
      } else if (currInstance > incomingInstance) {
        const deletedArr = state.fieldData.slice(insDiff);
        state.fieldData = state.fieldData.slice(0, insDiff);
        state.deletedField = [...deletedArr, ...state.deletedField];
      }

      state.instances.current = incomingInstance;
      state.type = type;
    },
    updateBlockData: (
      state,
      action: PayloadAction<{
        id: string;
        key: string;
        value: string;
      }>
    ) => {
      const { id, key, value } = action.payload;

      const newData = state.fieldData.map((data) => {
        if (data.id === id) {
          return { id, fields: { ...data.fields, [key]: value } };
        }
        return data;
      });

      state.fieldData = newData;
    },
    deleteBlock: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      state.deletedField = [
        ...state.fieldData.filter((d) => d.id === id),
        ...state.deletedField,
      ];
      state.fieldData = state.fieldData.filter((d) => d.id !== id);
      state.instances.current -= 1;
    },
    addBlock: (state) => {
      if (state.deletedField[0]) {
        state.fieldData = [...state.fieldData, state.deletedField[0]];
        state.deletedField.shift();
      } else {
        state.fieldData = [...state.fieldData, ...generatedFieldsArr(1)];
      }
      state.instances.current += 1;
    },
    clearBlock: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;

      state.fieldData = state.fieldData.map((d) => {
        if (d.id === id) return { ...d, fields: {} };
        return d;
      });
    },
    resetBlock: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;

      state.fieldData = state.fieldData.map((d) => {
        if (d.id === id) return { ...d, fields: getDefaultFields() };
        return d;
      });
    },
  },
});

export const {
  updateBlockData,
  initBlockData,
  deleteBlock,
  addBlock,
  clearBlock,
  resetBlock,
} = schemaSlice.actions;

export default schemaSlice.reducer;
